import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import clsx from 'clsx';
import { UserMultiSelect, type RoleConfig } from '../../components/UserSearchSelect';
import SearchInput from '../../components/SearchInput';
import SearchableSelect from '../../components/SearchableSelect';
import { useTableSelection } from '../../hooks/useTableSelection';
import BulkActionBar, { type BulkAction } from '../../components/BulkActionBar';
import BulkConfirmDialog from '../../components/BulkConfirmDialog';
import { API_ORIGIN } from '../../api/baseUrl';
import {
  bulkUpdateCustomerTier,
  bulkAssignCustomerOwners,
  bulkUpdateCustomerStatus,
  bulkDeleteCustomers,
} from '../../api/adminClient';

// ============================================================================
// TYPES
// ============================================================================

interface Tier {
  id: string;
  name: string;  // DB value (what gets stored)
  label: string; // Display label shown in UI
  icon: string;
  color: string;
}

interface CustomerOwner {
  user_id: string;
  name: string;
  email: string;
  is_primary: boolean;
}

interface Customer {
  id: string;
  name: string;
  display_name: string | null;
  website: string | null;
  country: string | null;
  location: string | null;
  industry: string | null;
  tier: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  owners?: CustomerOwner[];
}

interface User {
  id: string;
  name: string;
  email: string;
  level: string;
}

interface Country {
  code: string;
  name: string;
  region: string;
}

interface Industry {
  id: string;
  name: string;
  category: string;
}

// ============================================================================
// CONFIG
// ============================================================================

const tierColors = [
  { color: 'bg-purple-100 text-purple-800', icon: '🏢' },
  { color: 'bg-blue-100 text-blue-800', icon: '🏬' },
  { color: 'bg-gray-100 text-gray-800', icon: '🏠' },
  { color: 'bg-orange-100 text-orange-800', icon: '🔶' },
  { color: 'bg-green-100 text-green-800', icon: '🟢' },
  { color: 'bg-pink-100 text-pink-800', icon: '💎' },
  { color: 'bg-indigo-100 text-indigo-800', icon: '⚡' },
  { color: 'bg-teal-100 text-teal-800', icon: '🌊' },
];

const defaultTiers: Tier[] = [
  { id: 'tier-1', name: 'enterprise', label: 'Enterprise', icon: '🏢', color: 'bg-purple-100 text-purple-800' },
  { id: 'tier-2', name: 'mid-market', label: 'Mid-Market', icon: '🏬', color: 'bg-blue-100 text-blue-800' },
  { id: 'tier-3', name: 'smb', label: 'SMB', icon: '🏠', color: 'bg-gray-100 text-gray-800' },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getTierConfig(tierName: string | null, tiers: Tier[]): { label: string; color: string; icon: string } {
  if (!tierName) {
    return { label: 'Untiered', color: 'bg-yellow-50 text-yellow-700', icon: '—' };
  }
  const tier = tiers.find(t => t.name === tierName);
  if (tier) {
    return { label: tier.label, color: tier.color, icon: tier.icon };
  }
  return { label: tierName, color: 'bg-red-100 text-red-800', icon: '⚠️' };
}

function isTierMissing(tierName: string | null, tiers: Tier[]): boolean {
  if (!tierName) return false;
  return !tiers.some(t => t.name === tierName);
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function getCustomers(activeOnly: boolean = true): Promise<Customer[]> {
  const url = activeOnly
    ? `${API_ORIGIN}/api/customers`
    : `${API_ORIGIN}/api/customers?active=false`;
  const res = await fetch(url);
  const json = await res.json();
  return json.data || [];
}

async function createCustomer(data: Partial<Customer> & { owner_ids?: string[] }): Promise<Customer> {
  const res = await fetch(`${API_ORIGIN}/api/customers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create customer');
  return json.data;
}

async function updateCustomer(id: string, data: Partial<Customer> & { owner_ids?: string[] }): Promise<Customer> {
  const res = await fetch(`${API_ORIGIN}/api/customers/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update customer');
  return json.data;
}

async function deleteCustomer(id: string): Promise<void> {
  const res = await fetch(`${API_ORIGIN}/api/customers/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const json = await res.json();
    throw new Error(json.error || 'Failed to delete customer');
  }
}

async function getUsers(): Promise<User[]> {
  const res = await fetch(`${API_ORIGIN}/api/users`);
  const json = await res.json();
  return json.data || json || [];
}

async function getCountries(): Promise<Country[]> {
  const res = await fetch(`${API_ORIGIN}/api/command-center/countries`);
  const json = await res.json();
  return json.data || [];
}

async function getIndustries(): Promise<Industry[]> {
  const res = await fetch(`${API_ORIGIN}/api/command-center/industries`);
  const json = await res.json();
  return json.data || [];
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ITEMS_PER_PAGE = 20;

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Role config for UserMultiSelect
const ownerRoleConfig: Record<string, RoleConfig> = {
  executive: { icon: '👑', color: 'bg-purple-100 text-purple-800', label: 'Executive' },
  manager: { icon: '👔', color: 'bg-blue-100 text-blue-800', label: 'Manager' },
  sales: { icon: '💼', color: 'bg-orange-100 text-orange-800', label: 'Sales' },
};

export default function AdminCustomers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('');
  const [letterFilter, setLetterFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [showTierModal, setShowTierModal] = useState(false);
  const [tiers, setTiers] = useState<Tier[]>(defaultTiers);
  const [bulkDialog, setBulkDialog] = useState<string | null>(null);
  const [bulkTier, setBulkTier] = useState<string>('');
  const [bulkOwnerIds, setBulkOwnerIds] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch customers
  const { data: customers, isLoading, error } = useQuery({
    queryKey: ['customers'],
    queryFn: () => getCustomers(false),
  });

  // Fetch reference data
  const { data: users } = useQuery({ queryKey: ['users'], queryFn: getUsers });
  const { data: countries } = useQuery({ queryKey: ['countries'], queryFn: getCountries });
  const { data: industries } = useQuery({ queryKey: ['industries'], queryFn: getIndustries });

  // Mutations
  const createMutation = useMutation({
    mutationFn: createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setIsCreating(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Customer> & { owner_ids?: string[] } }) =>
      updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setSelectedCustomer(null);
    },
  });

  // Filter customers
  const filteredCustomers = customers?.filter((c) => {
    const matchesSearch =
      !searchTerm ||
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.industry?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = !tierFilter || (tierFilter === '__untiered__' ? !c.tier : c.tier === tierFilter);
    const displayName = (c.display_name || c.name).toUpperCase();
    const matchesLetter = !letterFilter || displayName.startsWith(letterFilter);
    const matchesStatus =
      !statusFilter ||
      (statusFilter === 'active' ? c.is_active !== false : c.is_active === false);
    return matchesSearch && matchesTier && matchesLetter && matchesStatus;
  });

  // Table selection
  const { selectedIds, toggleOne, toggleAll, clearSelection, isAllSelected, selectionCount } =
    useTableSelection(filteredCustomers || []);

  const selectedItems = (customers || []).filter((c) => selectedIds.has(c.id));

  const handleExportCSV = () => {
    const rows = (customers || []).map((c) => {
      const ownerEmails = c.owners?.map((o) => o.email).join(';') || '';
      return [c.name, c.tier || '', c.industry || '', c.website || '', c.country || '', c.location || '', ownerEmails, c.notes || '']
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',');
    });
    const csv = ['customer_name,tier,industry,website,country,location,account_owner_emails,notes', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers_export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const bulkActions: BulkAction[] = [
    { label: 'Change Tier', icon: '🏷️', onClick: () => setBulkDialog('tier') },
    { label: 'Assign Owners', icon: '👤', onClick: () => setBulkDialog('owners') },
    { label: 'Activate', onClick: () => setBulkDialog('activate') },
    { label: 'Deactivate', onClick: () => setBulkDialog('deactivate') },
    { label: 'Delete', icon: '🗑️', variant: 'danger', onClick: () => setBulkDialog('delete') },
  ];

  // Get all unique tier names from customers + defined tiers
  const existingTierNames = [...new Set(customers?.map((c) => c.tier).filter((t): t is string => Boolean(t)) || [])];
  const allTierNames: string[] = [...new Set([...tiers.map(t => t.name), ...existingTierNames])];

  // Stats
  const stats = customers
    ? {
        total: customers.length,
        byTier: allTierNames.reduce((acc, name) => {
          acc[name] = customers.filter((c) => c.tier === name).length;
          return acc;
        }, {} as Record<string, number>),
        untiered: customers.filter((c) => !c.tier).length,
        missingTier: customers.filter(c => c.tier && isTierMissing(c.tier, tiers)).length,
      }
    : null;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
        Failed to load customers: {(error as Error).message}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-500 mt-1">Manage customer accounts and ownership</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <span>📤</span>
            <span>Export CSV</span>
          </button>
          <button
            onClick={() => navigate('/admin/import?type=customers')}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <span>📥</span>
            <span>Import Customer Data</span>
          </button>
          <button
            onClick={() => setShowTierModal(true)}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <span>🏷️</span>
            <span>Update Tier</span>
          </button>
          <button
            onClick={() => {
              setIsCreating(true);
              setSelectedCustomer(null);
              setIsEditing(false);
              setShowModal(true);
            }}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <span>+</span> Add Customer
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <div
            className={clsx(
              "bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer transition-all hover:ring-2 hover:ring-primary-500",
              !tierFilter && "ring-2 ring-primary-500"
            )}
            onClick={() => { setTierFilter(''); setVisibleCount(ITEMS_PER_PAGE); }}
          >
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Customers</div>
          </div>
          {tiers.slice(0, 4).map((tier) => {
            const count = stats.byTier[tier.name] || 0;
            return (
              <div
                key={tier.id}
                className={clsx(
                  "bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all",
                  tierFilter === tier.name && "ring-2 ring-primary-500"
                )}
                onClick={() => {
                  setTierFilter(tierFilter === tier.name ? '' : tier.name);
                  setVisibleCount(ITEMS_PER_PAGE);
                }}
              >
                <div className="flex items-center gap-2">
                  <span>{tier.icon}</span>
                  <span className="text-2xl font-bold text-gray-900">{count}</span>
                </div>
                <div className="text-sm text-gray-500 truncate">{tier.label}</div>
              </div>
            );
          })}
          {stats.untiered > 0 && (
            <div
              className={clsx(
                "bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all",
                tierFilter === '__untiered__' && "ring-2 ring-primary-500"
              )}
              onClick={() => {
                setTierFilter(tierFilter === '__untiered__' ? '' : '__untiered__');
                setVisibleCount(ITEMS_PER_PAGE);
              }}
            >
              <div className="flex items-center gap-2">
                <span>—</span>
                <span className="text-2xl font-bold text-gray-900">{stats.untiered}</span>
              </div>
              <div className="text-sm text-gray-500">Untiered</div>
            </div>
          )}
          {stats.missingTier > 0 && (
            <div className="bg-red-50 rounded-xl shadow-sm border border-red-200 p-4">
              <div className="flex items-center gap-2">
                <span>⚠️</span>
                <span className="text-2xl font-bold text-red-700">{stats.missingTier}</span>
              </div>
              <div className="text-sm text-red-600">Missing Tier</div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <SearchInput
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setVisibleCount(ITEMS_PER_PAGE); }}
              placeholder="Search by name, location, or industry..."
            />
          </div>
          <SearchableSelect
            value={tierFilter}
            onChange={(v) => { setTierFilter(v); setVisibleCount(ITEMS_PER_PAGE); }}
            options={[
              { value: '', label: 'All Tiers' },
              ...tiers.map((tier) => ({ value: tier.name, label: `${tier.icon} ${tier.label}` })),
              { value: '__untiered__', label: '— Untiered' },
            ]}
            placeholder="All Tiers"
            searchPlaceholder="Filter tiers..."
            className="w-48"
          />
          <SearchableSelect
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setVisibleCount(ITEMS_PER_PAGE); }}
            options={[
              { value: '', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            placeholder="All Status"
            searchPlaceholder="Filter status..."
            className="w-48"
          />
          {(tierFilter || letterFilter || statusFilter) && (
            <button
              onClick={() => { setTierFilter(''); setLetterFilter(''); setStatusFilter('active'); }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* A-Z Letter Filter */}
        <div className="flex flex-wrap items-center gap-1 mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-400 mr-1">A-Z:</span>
          {ALPHABET.map((letter) => (
            <button
              key={letter}
              onClick={() => {
                setLetterFilter(letterFilter === letter ? '' : letter);
                setVisibleCount(ITEMS_PER_PAGE);
              }}
              className={clsx(
                'w-7 h-7 rounded text-xs font-medium transition-colors',
                letterFilter === letter
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-200'
              )}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div>
        {/* Customer List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : !filteredCustomers || filteredCustomers.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              {searchTerm || tierFilter || statusFilter ? 'No customers match your filters' : 'No customers found'}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleAll}
                      className="rounded border-gray-300"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Owners
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCustomers.slice(0, visibleCount).map((customer) => {
                  const config = getTierConfig(customer.tier, tiers);
                  const hasMissingTier = isTierMissing(customer.tier, tiers);
                  return (
                    <tr
                      key={customer.id}
                      className={clsx(
                        'cursor-pointer transition-colors',
                        selectedCustomer?.id === customer.id
                          ? 'bg-primary-50'
                          : 'hover:bg-gray-50',
                        !customer.is_active && 'opacity-50'
                      )}
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setIsEditing(false);
                        setIsCreating(false);
                        setShowModal(true);
                      }}
                    >
                      <td className="px-3 py-4 w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(customer.id)}
                          onChange={() => toggleOne(customer.id)}
                          className="rounded border-gray-300"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{customer.name}</div>
                          {customer.industry && (
                            <div className="text-sm text-gray-500">{customer.industry}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', config.color)}>
                            {config.icon} {config.label}
                          </span>
                          {hasMissingTier && (
                            <span className="text-red-500 text-sm" title="Tier no longer exists">
                              ⚠️
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {customer.location || customer.country || '-'}
                      </td>
                      <td className="px-6 py-4">
                        {customer.owners && customer.owners.length > 0 ? (
                          <div className="flex -space-x-2">
                            {customer.owners.slice(0, 3).map((owner) => (
                              <div
                                key={owner.user_id}
                                className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600"
                                title={owner.name}
                              >
                                {owner.name.charAt(0)}
                              </div>
                            ))}
                            {customer.owners.length > 3 && (
                              <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-500">
                                +{customer.owners.length - 3}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            No owners
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Show More Button */}
          {filteredCustomers && filteredCustomers.length > visibleCount && (
            <div className="p-4 border-t border-gray-100 text-center">
              <button
                onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Show More ({filteredCustomers.length - visibleCount} remaining)
              </button>
            </div>
          )}

          {/* Showing count */}
          {filteredCustomers && filteredCustomers.length > 0 && (
            <div className="p-3 bg-gray-50 border-t border-gray-100 text-center text-sm text-gray-500">
              Showing {Math.min(visibleCount, filteredCustomers.length)} of {filteredCustomers.length} customers
            </div>
          )}
        </div>

      </div>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectionCount={selectionCount}
        actions={bulkActions}
        onClear={clearSelection}
      />

      {/* Bulk Confirm Dialogs */}
      {bulkDialog === 'tier' && (
        <BulkConfirmDialog
          title="Change Customer Tier"
          description="Update the tier for selected customers"
          items={selectedItems.map((c) => ({ id: c.id, label: c.name }))}
          confirmLabel="Update Tier"
          confirmDisabled={!bulkTier}
          onConfirm={async () => {
            if (!bulkTier) return { succeeded: 0, failed: 0 };
            const res = await bulkUpdateCustomerTier(
              selectedItems.map((c) => c.id),
              bulkTier
            );
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => { setBulkDialog(null); setBulkTier(''); }}
        >
          <select
            value={bulkTier}
            onChange={(e) => setBulkTier(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select Tier</option>
            {tiers.map((tier) => (
              <option key={tier.id} value={tier.name}>
                {tier.icon} {tier.label}
              </option>
            ))}
          </select>
        </BulkConfirmDialog>
      )}

      {bulkDialog === 'owners' && (
        <BulkConfirmDialog
          title="Assign Owners"
          description="Add owners to selected customers"
          items={selectedItems.map((c) => ({ id: c.id, label: c.name }))}
          confirmLabel="Assign Owners"
          confirmDisabled={bulkOwnerIds.length === 0}
          onConfirm={async () => {
            if (bulkOwnerIds.length === 0) return { succeeded: 0, failed: 0 };
            const res = await bulkAssignCustomerOwners(
              selectedItems.map((c) => c.id),
              bulkOwnerIds
            );
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            setBulkOwnerIds([]);
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => { setBulkDialog(null); setBulkOwnerIds([]); }}
        >
          <UserMultiSelect
            users={(users || []).map((u) => ({ id: u.id, name: u.name, email: u.email, role: u.level }))}
            selectedIds={bulkOwnerIds}
            onChange={setBulkOwnerIds}
            roleConfig={ownerRoleConfig}
            placeholder="Search to add owner..."
          />
        </BulkConfirmDialog>
      )}

      {bulkDialog === 'activate' && (
        <BulkConfirmDialog
          title="Activate Customers"
          description="Set selected customers to active"
          items={selectedItems.map((c) => ({ id: c.id, label: c.name }))}
          confirmLabel="Activate"
          onConfirm={async () => {
            const res = await bulkUpdateCustomerStatus(selectedItems.map((c) => c.id), true);
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {bulkDialog === 'deactivate' && (
        <BulkConfirmDialog
          title="Deactivate Customers"
          description="Set selected customers to inactive"
          items={selectedItems.map((c) => ({ id: c.id, label: c.name }))}
          confirmLabel="Deactivate"
          variant="danger"
          onConfirm={async () => {
            const res = await bulkUpdateCustomerStatus(selectedItems.map((c) => c.id), false);
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {bulkDialog === 'delete' && (
        <BulkConfirmDialog
          title="Delete Customers"
          description="This will deactivate the selected customers"
          items={selectedItems.map((c) => ({ id: c.id, label: c.name }))}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={async () => {
            const res = await bulkDeleteCustomers(selectedItems.map((c) => c.id));
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {/* Tier Management Modal */}
      {showTierModal && (
        <TierManagementModal
          tiers={tiers}
          onClose={() => setShowTierModal(false)}
          onSave={(updatedTiers) => {
            setTiers(updatedTiers);
            setShowTierModal(false);
          }}
        />
      )}

      {/* Modal */}
      {showModal && (selectedCustomer || isCreating) && (
        <CustomerModal
          customer={isCreating ? null : selectedCustomer}
          isEditing={isEditing}
          isCreating={isCreating}
          tiers={tiers}
          users={users || []}
          countries={countries || []}
          industries={industries || []}
          onEdit={() => setIsEditing(true)}
          onCancel={() => {
            setIsEditing(false);
            setIsCreating(false);
          }}
          onClose={() => {
            setSelectedCustomer(null);
            setIsEditing(false);
            setIsCreating(false);
            setShowModal(false);
          }}
          onSave={(data) => {
            if (isCreating) {
              createMutation.mutate(data);
            } else if (selectedCustomer) {
              updateMutation.mutate({ id: selectedCustomer.id, data });
            }
            setShowModal(false);
          }}
          onDelete={() => {
            if (selectedCustomer && confirm('Are you sure you want to deactivate this customer?')) {
              deleteMutation.mutate(selectedCustomer.id);
              setShowModal(false);
            }
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

// ============================================================================
// CUSTOMER MODAL COMPONENT
// ============================================================================

interface CustomerModalProps {
  customer: Customer | null;
  isEditing: boolean;
  isCreating: boolean;
  tiers: Tier[];
  users: User[];
  countries: Country[];
  industries: Industry[];
  onEdit: () => void;
  onCancel: () => void;
  onClose: () => void;
  onSave: (data: Partial<Customer> & { owner_ids?: string[] }) => void;
  onDelete: () => void;
  isSaving: boolean;
}

function CustomerModal({
  customer,
  isEditing,
  isCreating,
  tiers,
  users,
  countries,
  industries,
  onEdit,
  onCancel,
  onClose,
  onSave,
  onDelete,
  isSaving,
}: CustomerModalProps) {
  const [form, setForm] = useState<{
    name: string;
    display_name: string;
    website: string;
    country: string;
    location: string;
    industry: string;
    tier: string;
    notes: string;
    owner_ids: string[];
  }>({
    name: customer?.name || '',
    display_name: customer?.display_name || '',
    website: customer?.website || '',
    country: customer?.country || '',
    location: customer?.location || '',
    industry: customer?.industry || '',
    tier: customer?.tier || '',
    notes: customer?.notes || '',
    owner_ids: customer?.owners?.map((o) => o.user_id) || [],
  });

  const isFormMode = isEditing || isCreating;
  const config = getTierConfig(customer?.tier || form.tier || null, tiers);
  const hasMissingTier = customer?.tier ? isTierMissing(customer.tier, tiers) : false;

  // All users for owner selection
  const salesUsers = users;

  const handleSave = () => {
    onSave({
      name: form.name,
      display_name: form.display_name || form.name,
      website: form.website || null,
      country: form.country || null,
      location: form.location || null,
      industry: form.industry || null,
      tier: form.tier || null,
      notes: form.notes || null,
      owner_ids: form.owner_ids,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            {isFormMode ? (
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Customer Name"
                className="text-xl font-bold text-gray-900 border border-gray-300 rounded px-2 py-1 w-full"
              />
            ) : (
              <h2 className="text-xl font-bold text-gray-900">{customer?.name}</h2>
            )}
            {!isFormMode && customer?.industry && (
              <p className="text-sm text-gray-500">{customer.industry}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        <div className="p-6">

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Tier */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
            {isFormMode ? (
              <div className="space-y-2">
                <select
                  value={tiers.some(t => t.name === form.tier) ? form.tier : '__custom__'}
                  onChange={(e) => {
                    if (e.target.value !== '__custom__') {
                      setForm({ ...form, tier: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                >
                  <option value="">Untiered</option>
                  {tiers.map((t) => (
                    <option key={t.id} value={t.name}>
                      {t.icon} {t.label}
                    </option>
                  ))}
                  {form.tier && !tiers.some(t => t.name === form.tier) && (
                    <option value="__custom__">Custom: {form.tier}</option>
                  )}
                </select>
                {(!form.tier || !tiers.some(t => t.name === form.tier)) && (
                  <>
                    <p className="text-xs text-gray-500">
                      Or type a custom tier name
                    </p>
                    <input
                      type="text"
                      value={form.tier}
                      onChange={(e) => setForm({ ...form, tier: e.target.value })}
                      placeholder="e.g. Strategic, Key Account..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                    />
                  </>
                )}
              </div>
            ) : customer?.tier ? (
              <div className="flex items-center gap-2 py-2">
                <span className={clsx('inline-flex items-center px-3 py-1 rounded-full text-sm font-medium', config.color)}>
                  {config.icon} {config.label}
                </span>
                {hasMissingTier && (
                  <span className="text-red-500" title="Tier no longer exists">⚠️</span>
                )}
              </div>
            ) : (
              <p className="text-gray-400 py-2">No tier assigned</p>
            )}
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            {isFormMode ? (
              <input
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            ) : customer?.website ? (
              <a
                href={customer.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                {customer.website}
              </a>
            ) : (
              <span className="text-gray-400 text-sm">-</span>
            )}
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            {isFormMode ? (
              <select
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Country</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-sm text-gray-600">{customer?.country || '-'}</span>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            {isFormMode ? (
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="City, State"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            ) : (
              <span className="text-sm text-gray-600">{customer?.location || '-'}</span>
            )}
          </div>

          {/* Industry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
            {isFormMode ? (
              <select
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Industry</option>
                {industries.map((i) => (
                  <option key={i.id} value={i.name}>
                    {i.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-sm text-gray-600">{customer?.industry || '-'}</span>
            )}
          </div>

          {/* Account Owners */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Owners</label>
            {isFormMode ? (
              <UserMultiSelect
                users={salesUsers.map((u) => ({ id: u.id, name: u.name, email: u.email, role: u.level }))}
                selectedIds={form.owner_ids}
                onChange={(ids) => setForm({ ...form, owner_ids: ids })}
                roleConfig={ownerRoleConfig}
                placeholder="Search to add account owner..."
              />
            ) : customer?.owners && customer.owners.length > 0 ? (
              <div className="space-y-2">
                {customer.owners.map((owner) => (
                  <div
                    key={owner.user_id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                      {owner.name.charAt(0)}
                    </div>
                    <span className="text-gray-700">{owner.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-gray-400 text-sm">No owners assigned</span>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            {isFormMode ? (
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal notes about this customer..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24 resize-none"
              />
            ) : customer?.notes ? (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{customer.notes}</p>
            ) : (
              <span className="text-gray-400 text-sm">-</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-6 mt-6 border-t border-gray-100">
          {isFormMode ? (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving || !form.name}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : isCreating ? 'Create Customer' : 'Save Changes'}
              </button>
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEdit}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Edit
              </button>
              <button
                onClick={onDelete}
                className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
              >
                Deactivate
              </button>
            </>
          )}
        </div>

        {/* Metadata */}
        {customer && !isFormMode && (
          <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
            <p>Created: {format(new Date(customer.created_at), 'MMM d, yyyy h:mm a')}</p>
            <p>Status: {customer.is_active ? 'Active' : 'Inactive'}</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TIER MANAGEMENT MODAL
// ============================================================================

interface TierManagementModalProps {
  tiers: Tier[];
  onClose: () => void;
  onSave: (tiers: Tier[]) => void;
}

function TierManagementModal({ tiers, onClose, onSave }: TierManagementModalProps) {
  const [localTiers, setLocalTiers] = useState<Tier[]>(tiers);
  const [newTierName, setNewTierName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const toSlug = (label: string) => label.trim().toLowerCase().replace(/\s+/g, '-');

  const handleAddTier = () => {
    if (!newTierName.trim()) return;
    const slug = toSlug(newTierName);
    if (localTiers.some(t => t.name === slug)) {
      alert('A tier with this name already exists');
      return;
    }

    const colorConfig = tierColors[localTiers.length % tierColors.length];
    const newTier: Tier = {
      id: `tier-${Date.now()}`,
      name: slug,
      label: newTierName.trim(),
      icon: colorConfig.icon,
      color: colorConfig.color,
    };

    setLocalTiers([...localTiers, newTier]);
    setNewTierName('');
  };

  const handleDeleteTier = (id: string) => {
    setLocalTiers(localTiers.filter(t => t.id !== id));
  };

  const handleEditTier = (id: string) => {
    const tier = localTiers.find(t => t.id === id);
    if (tier) {
      setEditingId(id);
      setEditingName(tier.label);
    }
  };

  const handleSaveEdit = () => {
    if (!editingId || !editingName.trim()) return;
    const slug = toSlug(editingName);
    if (localTiers.some(t => t.id !== editingId && t.name === slug)) {
      alert('A tier with this name already exists');
      return;
    }

    setLocalTiers(localTiers.map(t =>
      t.id === editingId ? { ...t, name: slug, label: editingName.trim() } : t
    ));
    setEditingId(null);
    setEditingName('');
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...localTiers];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setLocalTiers(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === localTiers.length - 1) return;
    const updated = [...localTiers];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setLocalTiers(updated);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage Tiers</h2>
            <p className="text-sm text-gray-500">Create and organize customer tiers</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Add New Tier */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Add New Tier</h3>
            <div className="flex gap-3">
              <input
                type="text"
                value={newTierName}
                onChange={(e) => setNewTierName(e.target.value)}
                placeholder="Tier name (e.g. Strategic, Key Account)..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleAddTier()}
              />
              <button
                onClick={handleAddTier}
                disabled={!newTierName.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          </div>

          {/* Tier List */}
          <div className="space-y-2">
            {localTiers.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No tiers yet. Add your first tier above.
              </div>
            ) : (
              localTiers.map((tier, index) => (
                <div
                  key={tier.id}
                  className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                >
                  <span className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', tier.color)}>
                    {tier.icon}
                  </span>
                  {editingId === tier.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit();
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span className="flex-1 font-medium text-gray-900">{tier.label}</span>
                  )}
                  <div className="flex items-center gap-1">
                    {editingId === tier.id ? (
                      <>
                        <button
                          onClick={handleSaveEdit}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === localTiers.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => handleEditTier(tier.id)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteTier(tier.id)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(localTiers)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            Save Tiers
          </button>
        </div>
      </div>
    </div>
  );
}
